import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Target, BookOpen, RefreshCw, TrendingUp, Star, Lightbulb } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { insightsService } from "@/services/insightsService";

interface GrowthSuggestion {
  type: "skill" | "mindset" | "practice" | "goal";
  title: string;
  description: string;
  actionable: string;
  timeframe: string;
  priority: "high" | "medium" | "low";
}

const GrowthAISuggestionsWidget = () => {
  const { user } = useUser();
  const [suggestions, setSuggestions] = useState<GrowthSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ALIGNED WITH web_old: loadGrowthSuggestions with backend API
  // Backend endpoint: GET /insights/growth-suggestions?forceRefresh=false
  // MIGRATED FROM web_old/src/components/growth/GrowthAISuggestionsWidget.tsx::loadGrowthSuggestions()
  const loadGrowthSuggestions = async (forceRefresh: boolean = false) => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // ALIGNED WITH web_old: Call backend endpoint (line 232)
      const response = await insightsService.getGrowthSuggestions(forceRefresh);
      const growthSuggestions = response?.suggestions || [];
      // ALIGNED WITH web_old: Backend returns format directly matching GrowthSuggestion interface
      // No mapping needed - backend format matches web_old format exactly
      setSuggestions(growthSuggestions);
    } catch (error) {
      console.error("Error loading growth suggestions:", error);
      // ALIGNED WITH web_old: Fallback on error (lines 236-245)
      setSuggestions([
        {
          type: "practice",
          title: "Enable Growth Insights",
          description: "Complete more check-ins to unlock personalized growth recommendations.",
          actionable: "Start with daily check-ins to build your growth data.",
          timeframe: "This week",
          priority: "medium"
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadGrowthSuggestions();
    }
  }, [user?.id]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "skill":
        return <BookOpen className="h-4 w-4 text-blue-500" />;
      case "mindset":
        return <Star className="h-4 w-4 text-purple-500" />;
      case "practice":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "goal":
        return <Target className="h-4 w-4 text-orange-500" />;
      default:
        return <Lightbulb className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "skill":
        return "border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-900/20";
      case "mindset":
        return "border-purple-200 bg-purple-50 dark:border-purple-800/50 dark:bg-purple-900/20";
      case "practice":
        return "border-green-200 bg-green-50 dark:border-green-800/50 dark:bg-green-900/20";
      case "goal":
        return "border-orange-200 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-900/20";
      default:
        return "border-gray-200 bg-gray-50 dark:border-border dark:bg-muted/20";
    }
  };

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-card dark:to-muted/50 border-purple-200 dark:border-border hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-muted rounded-lg">
              <Target className="h-4 w-4 text-purple-600 dark:text-primary" />
            </div>
            <div>
              <CardTitle className="text-base text-foreground">🎯 AI Growth Suggestions</CardTitle>
              <CardDescription className="text-xs">
                Personalized recommendations for your development
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadGrowthSuggestions(true)} // ALIGNED WITH web_old: forceRefresh: true (line 305)
            disabled={isLoading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 bg-white/60 animate-pulse rounded-lg"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.slice(0, 2).map((suggestion, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-lg border ${getTypeColor(suggestion.type)}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  {getTypeIcon(suggestion.type)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm text-foreground">{suggestion.title}</h4>
                      <Badge 
                        variant={suggestion.priority === "high" ? "destructive" : suggestion.priority === "medium" ? "default" : "secondary"}
                        className="text-xs px-1 py-0"
                      >
                        {suggestion.priority}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{suggestion.description}</p>
                    <p className="text-xs font-medium text-foreground mb-1">{suggestion.actionable}</p>
                    <p className="text-xs text-muted-foreground">{suggestion.timeframe}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GrowthAISuggestionsWidget;